import type { RegisterValues } from "@js/apps/auth/actions";
import { LocalStorage } from "@js/services";
import type { AccountType, User } from "@js/types/auth";

import type {
  NewClientInvitationDetails,
  NewFreelancerInvitationDetails,
} from "../../api";
import type { RegisterFormPrefill } from "../../utils";

type InitialValuesOptions = {
  newClientInvitationData?: NewClientInvitationDetails;
  newFreelancerInvitationData?: NewFreelancerInvitationDetails;
  isClientSignup?: boolean;
  freelancerRegisterPrefillData?: RegisterFormPrefill;
};

export const getInitialValues = (
  user: Pick<User, "first_name"> | null,
  options: InitialValuesOptions,
): Partial<RegisterValues> => {
  const {
    newClientInvitationData,
    newFreelancerInvitationData,
    isClientSignup,
    freelancerRegisterPrefillData,
  } = options;
  const initialValues: Partial<RegisterValues> = {};

  if (user?.first_name) {
    initialValues.first_name = user.first_name;
  }

  const invitation = newFreelancerInvitationData || newClientInvitationData;
  const prefilledData = invitation || freelancerRegisterPrefillData;
  if (prefilledData) {
    initialValues.first_name = prefilledData.first_name;
    initialValues.last_name = prefilledData.last_name;
    initialValues.email = prefilledData.email;
  }

  initialValues["joining_reasons"] = [ENUMS.JoiningReason.GET_A_JOB];
  initialValues["interests"] = [ENUMS.Interest.FREELANCING];

  if (isClientSignup) {
    initialValues.phone_number = { country_iso2: "US", phone_number: null };
  }

  return initialValues;
};

const getCreateAccountStepErrorMessage = (missingFields: string[]) => {
  return `You are missing ${missingFields.join(", ")} from previous steps.`;
};

export const getSubmissionErrorOnCreateAccountStep = (errors: object = {}) => {
  const errorEntries = Object.entries(errors);
  const currentErrors = Object.fromEntries(
    errorEntries.filter(([key]) => {
      if (key === "joining_reasons" || key === "interests") {
        return false;
      }
      return true;
    }),
  );

  const previousErrors = errorEntries
    .filter(([key]) => !currentErrors[key])
    .map(([key]) => key.replace("_", " "));

  if (previousErrors.length) {
    return {
      onboarding_step_error: getCreateAccountStepErrorMessage(previousErrors),
    };
  }

  if (!!currentErrors.phone_number) {
    return {
      ...currentErrors,
      ...currentErrors.phone_number,
    };
  }

  return currentErrors;
};

type Values = Pick<RegisterValues, "first_name" | "joining_reasons">;

export const validateSubmitGoals = (values: Partial<Values>) => {
  const { first_name, joining_reasons } = values;

  const isMissingFirstName = !first_name || !Boolean(first_name.trim().length);

  const isMissingJoiningReasons =
    !joining_reasons || !Boolean(joining_reasons.length);

  if (isMissingFirstName && isMissingJoiningReasons) {
    return {
      onboarding_step_error:
        "Please add your first name and choose at least one option above.",
      first_name: "Please add your first name",
    };
  }

  if (isMissingFirstName) {
    return {
      onboarding_step_error: "Please add your first name above.",
      first_name: "Please add your first name",
    };
  }

  if (isMissingJoiningReasons) {
    return {
      onboarding_step_error: "Please choose at least one option above.",
    };
  }

  return null;
};

export const updateLocalStorageData = () => {
  // clear data
  LocalStorage.removeItem(LocalStorage.keys.ONBOARDING_SAVED_DATA);
};

const createEmployerAccountRedirectLink = `https://www.usebraintrust.com/contact-sales?utm_source=self-serve`;
export const getCreateEmployerAccountRedirectLink = (referrer?: string) => {
  if (!referrer) {
    return createEmployerAccountRedirectLink;
  }

  return `${createEmployerAccountRedirectLink}&r=${encodeURIComponent(referrer)}`;
};

export const getValuesWithValidatePhoneNumber = <
  T extends { phone_number?: { phone_number?: string | null } },
>(
  values: T,
): T => {
  if (!values.phone_number) return values;

  if (
    values.phone_number.phone_number?.trim() === "" ||
    values.phone_number.phone_number === null
  ) {
    const { phone_number, ...valueWithoutPhoneNumber } = values;
    return valueWithoutPhoneNumber as T;
  }
  return values;
};

export const getFinishSignupRedirectUrl = ({
  finishSignupURL,
  nextQueryParam,
  accountType,
  invitationKeyQueryParam,
}: {
  accountType: AccountType;
  finishSignupURL: string | null;
  nextQueryParam?: string | null;
  invitationKeyQueryParam?: string | null;
}) => {
  if (accountType === "employer" && !invitationKeyQueryParam) {
    return "/onboarding/organization/";
  }

  return finishSignupURL || nextQueryParam || "/";
};
